import type { CampaignRegime } from '../../campaign/campaign.types';
import type { DealPhase } from '../../feed/deal.types';
import type { SecurityClass } from '../../feed/feed.types';
import {
  barOf,
  closedDateOf,
  compactMoney,
  dateTimeZone,
  daysBetween,
  daysLeft,
  fullDate,
  fullMoney,
  fundedPct,
  hourMinute,
  LONDON,
  parts,
  pctText,
  safeZone,
  sameAsName,
  scaleOf,
  shortDay,
  stated,
  tileStyle,
  initials,
  urgencyOf,
  weekdayDay,
  type Bar,
  type UrgencyTone,
} from './raise-figures';
import { BRAND_NAME } from '../../shared/brand';
import { hubPath } from './browse';
import { aboutDate, aboutRaiseView, type SimilarRaises } from './raise-about';
import { raiseFaqView } from './raise-faq';
import { logoImg, type LogoState } from './logo';

export {
  compactMoney,
  daysBetween,
  fullDate,
  fullMoney,
  LONDON,
  safeZone,
  shortDay,
};

/**
 * The public deal page, /raises/{slug} (views/raise.hbs): every figure, date
 * and state it shows about one raise, worked out from the facts
 * RaisePageService loads. Pure, and free of anything the browser cannot
 * take, because the page's script (client/raise.ts) runs `clockView` again
 * on the reader's own clock and time zone. The server's first paint uses the
 * signed-in reader's saved zone, or London's.
 *
 * Nothing here fills a gap: a figure the source does not state is null, and
 * the line or tile that would show it is left out, never shown as 0.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** After this long without a poll seeing the raise, its figures are stale. */
export const STALE_AFTER_MS = 2 * HOUR;

/** What the server knows about the raise, as it stands. */
export interface RaiseFacts {
  id: string;
  /** Its public slug: /raises/{slug}. */
  slug: string;
  /**
   * The public slug of the raise's canonical page: its own, or the listing's
   * where the row is a Form C a platform listing covers, since both pages
   * are the same raise.
   */
  canonicalSlug: string;
  /** The portal's id, for its other raises; null for a filing that names none. */
  platformId: string | null;
  /** Raise Sonar is paid for what it sends the platform (platform.affiliate). */
  affiliate: boolean;
  /** Its deal page in the dashboard, under the dashboard's base: "deals/crowdcube/loopwell". */
  appPath: string;
  name: string;
  tagline: string | null;
  /**
   * The logo's address on the platform, which the logo stores read it
   * from; never drawn on a page (see `logo` in RaisePageInput).
   */
  logoUrl: string | null;
  /**
   * The research reader's summary of the campaign page (an `ai` fact with
   * its quote, src/shared/research/wire.ts); null where there is none.
   */
  summary: string | null;
  /**
   * The campaign page `summary` was read from: the raise's own or, for a
   * Form C, its listing's. Null with no summary.
   */
  pitchUrl: string | null;
  /** The platform the money goes to: "Crowdcube". */
  platformName: string;
  /** Where the platform is based, as a name; residents-only raises are open to it. */
  platformCountry: string | null;
  /** Its chip's letters, the feed's monogram(): "Cr". */
  platformMono: string;
  /** Who reported the raise, for the provenance line: "Crowdcube", "SEC EDGAR". */
  sourceName: string;
  /** The raise's page on the platform, or the filing for an EDGAR row. */
  url: string;
  /** An EDGAR row: `url` is the SEC's filing, so there is no pitch to link. */
  filing: boolean;
  /** The SEC form behind an EDGAR row ("C", "C/A"); null elsewhere. */
  filingForm: string | null;
  /** The rules it is offered under. */
  regime: CampaignRegime;
  /** The feed's bucket for its security ("Preferred"), which `instrument` folds into "Equity". */
  securityClass: SecurityClass | null;
  /** "Equity", "SAFE" (instrumentOf in src/shared/research/compute.ts). */
  instrument: string | null;
  /** The valuation is a cap, not a pre-money: SAFE, convertible, ASA. */
  converts: boolean;
  /** Its first tag, as the platform words it: what the page says the platform lists it under. */
  sector: string | null;
  /** The site's sector it is linked to, by what its tags come to; null for none or "other". */
  hubSector: { slug: string; name: string } | null;
  /** The issuer's country, as a name: "United Kingdom". */
  country: string | null;
  /** The same country's ISO code, "GB", which names its country page. */
  countryCode: string | null;
  /** ISO code; the dashboard's money() assumes USD where none is stated. */
  currency: string | null;
  raised: number | null;
  /** The minimum the raise must reach: Crowdcube's target, FunderNation's Fundingschwelle. */
  target: number | null;
  /** The most it will take, where the source states it. */
  max: number | null;
  investors: number | null;
  minInvestment: number | null;
  /** Pre-money valuation, or a cap where `converts`. */
  valuation: number | null;
  /** true: open beyond the platform's home market; false: its residents only; null: not stated. */
  openToForeign: boolean | null;
  launchAt: Date | null;
  closeAt: Date | null;
  /** When a poll last saw the raise: campaign.last_seen_at. */
  checkedAt: Date;
  /**
   * When what the page says last changed, which a poll that finds nothing
   * new leaves alone: campaign.last_material_change_at.
   */
  changedAt: Date;
  phase: DealPhase;
  /** The latest move of the close date a poll saw, if any. */
  dateMove: { from: Date; to: Date } | null;
  /** When it ended, for a raise that has (endedAtOf); null for one nobody dated. */
  endedAt?: Date | null;
  /** How a withdrawal is known, where an event says. */
  withdrawnBy?: WithdrawnBy | null;
}

/** A Form C-W filed with the SEC, or gone from its platform's listing. */
export type WithdrawnBy = 'FORM_C_W' | 'LISTING_REMOVED';

/** What the clock half needs, as the page hands it to its script. */
export interface RaiseClock {
  phase: DealPhase;
  name: string;
  platformName: string;
  sourceName: string;
  launchAt: string | null;
  closeAt: string | null;
  checkedAt: string;
  changedAt: string;
  /** When it ended, where that is known. */
  endedAt?: string | null;
  /**
   * When the reader began following the raise, which decides the reminders
   * that are theirs (stripOf); null when they do not follow it.
   */
  followedAt: string | null;
}

export function clockOf(
  facts: RaiseFacts,
  followedAt: Date | null = null,
): RaiseClock {
  return {
    phase: facts.phase,
    name: facts.name,
    platformName: facts.platformName,
    sourceName: facts.sourceName,
    launchAt: facts.launchAt?.toISOString() ?? null,
    closeAt: facts.closeAt?.toISOString() ?? null,
    checkedAt: facts.checkedAt.toISOString(),
    changedAt: facts.changedAt.toISOString(),
    endedAt: facts.endedAt?.toISOString() ?? null,
    followedAt: followedAt?.toISOString() ?? null,
  };
}

// ---- Money ------------------------------------------------------------------------

/**
 * "£6.2M" from a million up (one decimal, none when it is .0), "£1.4B" from
 * a billion; full below. By hand, since en-GB's compact form writes "£6.2m".
 */
export function shortMoney(amount: number, currency: string | null): string {
  const size = Math.abs(amount);
  if (size < 1_000_000) return fullMoney(amount, currency);
  const [scale, unit] =
    size >= 999_950_000 ? [1_000_000_000, 'B'] : [1_000_000, 'M'];
  try {
    const figure = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency ?? 'USD',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(amount / scale);
    return `${figure}${unit}`;
  } catch {
    return fullMoney(amount, currency);
  }
}

// ---- Figures ----------------------------------------------------------------------

export interface Progress {
  /** "82%": rounded down, so a raise is never shown funded before it is. */
  shown: string;
  /** Beside it: "funded" against the target, "of maximum" against the maximum. */
  of: string;
  /** The bar's width in percent, unrounded and at most 100. */
  bar: number;
  /** The target's place on a bar that spans to the maximum; null without one. */
  tick: number | null;
  /** aria-valuenow, within aria-valuemax's 100. */
  now: number;
  /** "82% of target raised", "12% of maximum raised". */
  label: string;
  /**
   * Under the bar: "£0", then "£87,700 to target" or "Target reached" (or,
   * on a bar to the maximum, "Target €90,000 met"), then the bar's end.
   */
  start: string;
  middle: string;
  end: string;
}

/** The bar and its labels; null without a raised figure and a scale, or before launch. */
export function progressOf(
  raised: number | null,
  target: number | null,
  max: number | null,
  currency: string | null,
): Progress | null {
  const scale = scaleOf(target, max);
  if (raised === null || !Number.isFinite(raised) || !scale) return null;
  const exact = (raised / scale.amount) * 100;
  const whole = Math.floor(exact);
  const shown = `${whole.toLocaleString('en-GB')}%`;
  const twoPlaces = (n: number) => Math.round(n * 100) / 100;
  const common = {
    shown,
    bar: Math.min(100, twoPlaces(exact)),
    now: Math.min(100, whole),
    start: fullMoney(0, currency),
    end: fullMoney(scale.amount, currency),
  };

  if (scale.basis === 'target') {
    return {
      ...common,
      of: 'funded',
      tick: null,
      label: `${shown} of target raised`,
      middle:
        raised >= scale.amount
          ? 'Target reached'
          : `${fullMoney(scale.amount - raised, currency)} to target`,
    };
  }
  return {
    ...common,
    of: 'of maximum',
    tick: stated(target) ? twoPlaces((target / scale.amount) * 100) : null,
    label: `${shown} of maximum raised`,
    middle:
      raised >= scale.amount
        ? 'Maximum reached'
        : stated(target)
          ? `Target ${fullMoney(target, currency)} ${raised >= target ? 'met' : 'not met yet'}`
          : `${fullMoney(scale.amount - raised, currency)} to the maximum`,
  };
}

/**
 * "At target: £6.7M post-money · 7.46% equity offered": what the target buys
 * at the stated pre-money. Only for a priced round; a SAFE's cap is not a
 * pre-money, so it gets no such line.
 */
export function atTargetLine(
  valuation: number | null,
  target: number | null,
  currency: string | null,
  converts: boolean,
): string | null {
  if (converts || !stated(valuation) || !stated(target)) return null;
  const post = valuation + target;
  const equity = ((target / post) * 100).toFixed(2);
  return `At target: ${shortMoney(post, currency)} post-money · ${equity}% equity offered`;
}

export interface Tile {
  label: string;
  value: string;
}

/**
 * The stats card's tiles, before "Time left" (which is the clock's):
 * investors, minimum, and the valuation, each only where it is stated.
 */
export function tilesOf(facts: RaiseFacts): Tile[] {
  const tiles: Tile[] = [];
  if (facts.investors !== null && facts.investors >= 0) {
    tiles.push({
      label: 'Investors',
      value: facts.investors.toLocaleString('en-GB'),
    });
  }
  if (stated(facts.minInvestment)) {
    tiles.push({
      label: 'Minimum investment',
      value: fullMoney(facts.minInvestment, facts.currency),
    });
  }
  if (stated(facts.valuation)) {
    tiles.push({
      label: facts.converts ? 'Valuation cap' : 'Pre-money valuation',
      value: shortMoney(facts.valuation, facts.currency),
    });
  }
  return tiles;
}

// ---- Dates ------------------------------------------------------------------------

/** "22 Oct". */
function dayMonth(date: Date, zone: string): string {
  const p = parts(date, zone, { day: 'numeric', month: 'short' });
  return `${p.day} ${p.month}`;
}

/** "Mon 19 Oct · 23:59". */
export function dayAndTime(date: Date, zone: string): string {
  return `${shortDay(date, zone)} · ${hourMinute(date, zone)}`;
}

// ---- The clock ------------------------------------------------------------------

/** ○ more than 7 days, ▲ 7 days or less, ■ 72 hours or less. */
export type Tier = 'calm' | 'soon' | 'final';

export function tierOf(left: number): Tier {
  if (left <= 72 * HOUR) return 'final';
  if (left <= 7 * DAY) return 'soon';
  return 'calm';
}

export const TIER_GLYPH: Record<Tier, string> = {
  calm: '○',
  soon: '▲',
  final: '■',
};

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

/**
 * The "Time left" tile: whole calendar days in the reader's zone ("6 days",
 * "1 day"), hours inside the last 24 ("18 hours"), and "Closes today" inside
 * the last hour.
 */
export function timeLeftText(close: Date, now: Date, zone: string): string {
  const left = close.getTime() - now.getTime();
  if (left < HOUR) return 'Closes today';
  if (left < DAY) return plural(Math.floor(left / HOUR), 'hour', 'hours');
  return plural(Math.max(1, daysBetween(now, close, zone)), 'day', 'days');
}

/**
 * The timeline card's heading for a live raise: days, as the tile counts
 * them, until the last 72 hours, which count in hours.
 */
export function closesInText(close: Date, now: Date, zone: string): string {
  const left = close.getTime() - now.getTime();
  if (left < HOUR) return 'Closes today';
  if (left <= 72 * HOUR) {
    return `Closes in ${plural(Math.floor(left / HOUR), 'hour', 'hours')}`;
  }
  return `Closes in ${timeLeftText(close, now, zone)}`;
}

/**
 * Day of the campaign, 1-based, out of its length in calendar days: launch
 * day is day 1, and the count stops at the length on the last day.
 */
export function campaignDay(
  launch: Date,
  close: Date,
  now: Date,
  zone: string,
): { day: number; total: number } | null {
  const total = daysBetween(launch, close, zone);
  if (total < 1) return null;
  const day = daysBetween(launch, now, zone) + 1;
  return { day: Math.min(total, Math.max(1, day)), total };
}

/** The follow's closing reminders: the watchlist's (REMINDER_HOURS). */
export const REMINDERS = [
  {
    kind: '7d',
    hours: 168,
    left: '7 days left',
    mark: '7-day',
    lead: '7 days',
  },
  {
    kind: '72h',
    hours: 72,
    left: '72 hours left',
    mark: '72-hour',
    lead: '72 hours',
  },
  {
    kind: '24h',
    hours: 24,
    left: '24 hours left',
    mark: '24-hour',
    lead: '24 hours',
  },
] as const;

export type ReminderKind = (typeof REMINDERS)[number]['kind'];

export interface Reminder {
  kind: ReminderKind;
  at: Date;
  /** Its time has come: the reminder went to whoever follows the raise. */
  sent: boolean;
  /** "72 hours left", for the alert card's schedule. */
  left: string;
  /** "Mon 19 Oct · 23:59". */
  when: string;
  /** "Mon 19 Oct". */
  day: string;
  /** "72-hour alert, Mon 19 Oct": the timeline marker's title. */
  title: string;
  /** "72 hours", for the legend. */
  lead: string;
  /** "7-day", for the legend's sent line. */
  mark: string;
}

/**
 * The reminders a follow gets for a raise closing at `close`. One whose
 * moment comes before the raise opened never goes, so it is left out.
 */
export function remindersOf(
  close: Date,
  launch: Date | null,
  now: Date,
  zone: string,
): Reminder[] {
  return REMINDERS.flatMap((r) => {
    const at = new Date(close.getTime() - r.hours * HOUR);
    if (launch && at < launch) return [];
    const day = shortDay(at, zone);
    return [
      {
        kind: r.kind,
        at,
        sent: at <= now,
        left: r.left,
        when: dayAndTime(at, zone),
        day,
        title: `${r.mark} alert, ${day}`,
        lead: r.lead,
        mark: r.mark,
      },
    ];
  });
}

/** "a", "a and b", "a, b and c". */
function listed(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * The timeline's legend: "7-day alert sent Thu 15 Oct", and "Next alerts:
 * 72 hours (Mon 19 Oct) and 24 hours (Wed 21 Oct) before close".
 */
export function legendOf(reminders: Reminder[]): {
  sent: string | null;
  next: string | null;
} {
  const sent = reminders.filter((r) => r.sent);
  const ahead = reminders.filter((r) => !r.sent);
  return {
    sent: sent.length
      ? sent.map((r) => `${r.mark} alert sent ${r.day}`).join(' · ')
      : null,
    next: ahead.length
      ? `Next alert${ahead.length === 1 ? '' : 's'}: ${listed(
          ahead.map((r) => `${r.lead} (${r.day})`),
        )} before close`
      : null,
  };
}

/** "4 min ago", "3 h ago", "9 d ago". */
export function agoText(at: Date, now: Date): string {
  const ms = Math.max(0, now.getTime() - at.getTime());
  if (ms < MINUTE) return 'just now';
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)} min ago`;
  if (ms < 48 * HOUR) return `${Math.floor(ms / HOUR)} h ago`;
  return `${Math.floor(ms / DAY)} d ago`;
}

/** A raise that has ended: its figures are final, not stale. */
export const ENDED: readonly DealPhase[] = ['CLOSED', 'WITHDRAWN', 'DELISTED'];

/**
 * "↳ Crowdcube · checked 4 min ago". Past two hours on a raise still open,
 * "last checked 3 h ago", in the stale colour; an ended raise says "last
 * checked" without the warning.
 */
export function provenanceOf(
  sourceName: string,
  checkedAt: Date,
  phase: DealPhase,
  now: Date,
): { text: string; stale: boolean } {
  const ago = agoText(checkedAt, now);
  const old = now.getTime() - checkedAt.getTime() > STALE_AFTER_MS;
  const stale = old && !ENDED.includes(phase);
  return {
    text: `↳ ${sourceName} · ${old ? 'last checked' : 'checked'} ${ago}`,
    stale,
  };
}

export interface Marker {
  kind: ReminderKind;
  /** Along the track, in percent from launch. */
  pos: number;
  sent: boolean;
  title: string;
}

export interface Track {
  /** Today along the track, in percent from launch. */
  today: number;
  markers: Marker[];
  /** "Launched Tue 22 Sep", "Closes Thu 22 Oct". */
  launched: string;
  closes: string;
  /** The phone's: "22 Sep", "22 Oct". */
  launchedShort: string;
  closesShort: string;
}

/** The pill: shape and colour, and the word they stand for. */
export interface Pill {
  glyph: string;
  text: string;
  /** Its tone: a tier, or neutral. */
  tone: Tier | 'neutral';
}

export interface ClockView {
  pill: Pill;
  heading: string;
  /** "Thursday 22 October 2026 at 23:59 BST", under the heading. */
  dateLine: string | null;
  /** The heading and date line as one sentence, for a screen reader. */
  countdownLabel: string;
  /** The "Time left" tile, live raises with a close date only. */
  timeLeft: { glyph: string; text: string; tier: Tier } | null;
  /** "24/30", and the phone's "Day 24 of 30". */
  day: { short: string; long: string } | null;
  track: Track | null;
  legend: { sent: string | null; next: string | null };
  /** The alert card's schedule: reminders still ahead. */
  schedule: { kind: ReminderKind; left: string; when: string }[];
  provenance: { text: string; stale: boolean };
  /** After "Last updated": "30 Oct 2026, 14:05 GMT" (RaiseFacts.changedAt). */
  updated: string;
  /** A follower's close reminders, for a raise still to close by a date. */
  strip: Strip | null;
}

const percentAlong = (at: Date, launch: Date, close: Date): number => {
  const span = close.getTime() - launch.getTime();
  const along = (at.getTime() - launch.getTime()) / span;
  return Math.round(Math.min(1, Math.max(0, along)) * 10_000) / 100;
};

// ---- Your close reminders ---------------------------------------------------------

/** A node on the reminders strip: one of the follower's alerts, or the close. */
export interface StripNode {
  kind: ReminderKind | 'close';
  /** Along the rail, in percent from its start to the close. */
  pos: number;
  /** Sent, the next to go, one after that, or the close itself. */
  state: 'sent' | 'next' | 'ahead' | 'close';
  /** "7-day alert", "Next: 24 hours", "Close". */
  title: string;
  /** "sent Wed 14 Oct", "Tue 20 Oct", "Wed 21". */
  date: string;
  /**
   * How its label sits against it: from the node (the first), up to it (the
   * close, and the next alert, so its label never runs into the close's),
   * or centred under it.
   */
  align: 'start' | 'center' | 'end';
}

/** "Your close reminders": a follower's alerts before the close, on one rail. */
export interface Strip {
  /** "▲ Closes Wed 21 Oct", in the tone of the days left (urgencyOf). */
  closes: string;
  tone: UrgencyTone;
  /** The ink fill, from the rail's start up to now, in percent. */
  fill: number;
  /** Now on the rail, in percent; null while the rail has not begun. */
  now: number | null;
  nodes: StripNode[];
}

/** The rail runs from the first closing reminder, a week out, to the close. */
const STRIP_SPAN = REMINDERS[0].hours * HOUR;

/**
 * The reader's close reminders: the closing reminders a follow gets
 * (remindersOf), less any whose moment came before they followed, which
 * were never theirs, then the close. Each sits on the rail by its moment.
 */
export function stripOf(
  close: Date,
  launch: Date | null,
  followedAt: Date | null,
  now: Date,
  zone: string,
): Strip {
  const start = close.getTime() - STRIP_SPAN;
  const along = (at: number) =>
    Math.round(Math.min(1, Math.max(0, (at - start) / STRIP_SPAN)) * 10_000) /
    100;
  const reminders = remindersOf(close, launch, now, zone).filter(
    (r) => !followedAt || r.at >= followedAt,
  );
  const next = reminders.find((r) => !r.sent);
  const nodes: StripNode[] = reminders.map((r) => {
    const state = r.sent ? 'sent' : r === next ? 'next' : 'ahead';
    return {
      kind: r.kind,
      pos: along(r.at.getTime()),
      state,
      title: state === 'next' ? `Next: ${r.lead}` : `${r.mark} alert`,
      date: state === 'sent' ? `sent ${r.day}` : r.day,
      align: state === 'next' ? 'end' : 'center',
    };
  });
  if (nodes.length > 0) nodes[0] = { ...nodes[0], align: 'start' };
  nodes.push({
    kind: 'close',
    pos: 100,
    state: 'close',
    title: 'Close',
    date: weekdayDay(close, zone),
    align: 'end',
  });
  const urgency = urgencyOf(daysLeft(close, now, zone));
  return {
    closes: `${urgency.tone === 'calm' ? '' : '▲ '}Closes ${shortDay(close, zone)}`,
    tone: urgency.tone,
    fill: along(now.getTime()),
    now: now.getTime() < start ? null : along(now.getTime()),
    nodes,
  };
}

/** What the page says that depends on the clock and the reader's zone. */
export function clockView(
  clockIn: RaiseClock,
  now: Date,
  zoneIn: string | null,
): ClockView {
  const zone = safeZone(zoneIn);
  const launch = clockIn.launchAt ? new Date(clockIn.launchAt) : null;
  const close = clockIn.closeAt ? new Date(clockIn.closeAt) : null;
  const provenance = provenanceOf(
    clockIn.sourceName,
    new Date(clockIn.checkedAt),
    clockIn.phase,
    now,
  );
  const updated = dateTimeZone(new Date(clockIn.changedAt), zone);
  const empty = {
    timeLeft: null,
    day: null,
    track: null,
    legend: { sent: null, next: null },
    schedule: [],
    provenance,
    updated,
    strip: null,
  };
  const dateLine = close ? fullDate(close, zone) : null;

  if (clockIn.phase === 'CLOSED') {
    const closed = closedDateOf(
      {
        phase: clockIn.phase,
        closeAt: close,
        endedAt: clockIn.endedAt ? new Date(clockIn.endedAt) : null,
      },
      now,
    );
    const heading = closed ? `Closed on ${shortDay(closed, zone)}` : 'Closed';
    // Its close date to the minute; a day a poll noticed, only as a day.
    const closedLine = !closed
      ? null
      : closed.getTime() === close?.getTime()
        ? dateLine
        : aboutDate(closed, zone);
    return {
      ...empty,
      pill: { glyph: '✓', text: 'Closed', tone: 'neutral' },
      heading,
      dateLine: closedLine,
      countdownLabel: [heading, closedLine].filter(Boolean).join(', '),
    };
  }
  if (clockIn.phase === 'WITHDRAWN' || clockIn.phase === 'DELISTED') {
    // As the dashboard's deal page names them (web/src/deal/model.ts).
    const text =
      clockIn.phase === 'WITHDRAWN' ? 'Withdrawn' : 'No longer listed';
    return {
      ...empty,
      pill: { glyph: '', text, tone: 'neutral' },
      heading: text,
      dateLine: null,
      countdownLabel: text,
    };
  }

  const reminders = close ? remindersOf(close, launch, now, zone) : [];
  const schedule = reminders
    .filter((r) => !r.sent)
    .map(({ kind, left, when }) => ({ kind, left, when }));
  const followedAt = clockIn.followedAt ? new Date(clockIn.followedAt) : null;
  const strip =
    close && close > now ? stripOf(close, launch, followedAt, now, zone) : null;

  if (clockIn.phase === 'UPCOMING') {
    const opens = launch && launch > now ? launch : null;
    const heading = opens
      ? `Opens ${shortDay(opens, zone)}`
      : `Opens on ${clockIn.platformName}`;
    const line = opens ? fullDate(opens, zone) : null;
    return {
      ...empty,
      schedule,
      strip,
      pill: { glyph: '○', text: 'Upcoming', tone: 'neutral' },
      heading,
      dateLine: line,
      countdownLabel: [heading, line].filter(Boolean).join(', '),
    };
  }

  // Live, or a status the sources do not settle: open until it says not.
  if (!close || close <= now) {
    const heading = 'Close date not published';
    return {
      ...empty,
      pill: { glyph: '○', text: 'Live', tone: 'calm' },
      heading,
      dateLine: null,
      countdownLabel: heading,
    };
  }

  const tier = tierOf(close.getTime() - now.getTime());
  const glyph = TIER_GLYPH[tier];
  const heading = closesInText(close, now, zone);
  const day = launch ? campaignDay(launch, close, now, zone) : null;
  const track: Track | null =
    launch && launch < close
      ? {
          today: percentAlong(now, launch, close),
          markers: reminders.map((r) => ({
            kind: r.kind,
            pos: percentAlong(r.at, launch, close),
            sent: r.sent,
            title: r.title,
          })),
          launched: `Launched ${shortDay(launch, zone)}`,
          closes: `Closes ${shortDay(close, zone)}`,
          launchedShort: dayMonth(launch, zone),
          closesShort: dayMonth(close, zone),
        }
      : null;

  return {
    pill: {
      glyph,
      text: tier === 'calm' ? 'Live' : 'Closing soon',
      tone: tier,
    },
    heading,
    dateLine,
    countdownLabel: `${heading}, ${dateLine}`,
    timeLeft: { glyph, text: timeLeftText(close, now, zone), tier },
    day: day
      ? {
          short: `${day.day}/${day.total}`,
          long: `Day ${day.day} of ${day.total}`,
        }
      : null,
    track,
    legend: legendOf(reminders),
    schedule,
    provenance,
    updated,
    strip,
  };
}

// ---- The page ---------------------------------------------------------------------

/** The raise's page on the platform, marked as sent from here. No affiliate tag. */
export function outboundUrl(url: string): string {
  try {
    const out = new URL(url);
    out.searchParams.set('utm_source', 'raisesonar');
    return out.href;
  } catch {
    return url;
  }
}

/**
 * The rel of a link that leaves for a platform in a new tab:
 * "sponsored nofollow" where Raise Sonar is paid for what it sends there
 * (platform.affiliate), as search engines ask of paid links, and only then.
 * A filing's link is SEC EDGAR's, never the platform's, so pass false.
 */
export function outboundRel(affiliate: boolean): string {
  return affiliate ? 'sponsored nofollow noopener' : 'noopener';
}

/**
 * Where the follow buttons go signed out: sign-in or sign-up, then back to
 * this page holding `?follow=`, which the page's script turns into the
 * follow once the reader is signed in (client/raise.ts).
 */
export function followReturn(facts: Pick<RaiseFacts, 'id' | 'slug'>): string {
  return `/raises/${encodeURIComponent(facts.slug)}?follow=${facts.id}`;
}

export function signUpHref(facts: Pick<RaiseFacts, 'id' | 'slug'>): string {
  return `/signup?redirect_url=${encodeURIComponent(followReturn(facts))}`;
}

export function logInHref(facts: Pick<RaiseFacts, 'id' | 'slug'>): string {
  return `/login?redirect_url=${encodeURIComponent(followReturn(facts))}`;
}

/**
 * "Loopwell is raising on Crowdcube": raising while it is open or about to
 * be, raised once it has closed, and plainly "on" a platform it has left.
 */
export function onPlatform(
  facts: Pick<RaiseFacts, 'name' | 'platformName' | 'phase'>,
): string {
  if (facts.phase === 'CLOSED')
    return `${facts.name} raised on ${facts.platformName}`;
  if (facts.phase === 'WITHDRAWN' || facts.phase === 'DELISTED') {
    return `${facts.name} on ${facts.platformName}`;
  }
  return `${facts.name} is raising on ${facts.platformName}`;
}

// ---- The title and the meta description -------------------------------------------

/**
 * The most of a title, before " · Raise Sonar", that a search result shows
 * whole. The page's og:title and twitter:title are the same title
 * (partials/head.hbs).
 */
export const TITLE_LENGTH = 65;

/** The most of a meta description a search result shows (and og:description). */
export const DESCRIPTION_LENGTH = 155;

/** The least of a tagline worth keeping when the description runs long. */
const TAGLINE_FLOOR = 60;

/**
 * "Loopwell equity crowdfunding raise on Crowdcube — 82% funded · Raise
 * Sonar". The status after the dash is as much of it as keeps the title
 * within TITLE_LENGTH, most telling part first ("82% funded, closes 22 Oct",
 * then "82% funded", then none); a name too long for any keeps the rest
 * whole, since a search result cuts the end, not the name.
 *
 * A loan is a "crowdfunding raise": the feed leaves loans out as no equity
 * (OUT_OF_SCOPE in src/feed/feed.rules.ts), so the title does not call one
 * that. A Form C whose portal we do not know names no platform, since "on
 * SEC EDGAR" is where it was filed, not where the money goes.
 */
export function pageTitle(facts: RaiseFacts, zone: string, now: Date): string {
  const kind =
    facts.instrument === 'Bond / loan'
      ? 'crowdfunding raise'
      : 'equity crowdfunding raise';
  const on =
    facts.filing && facts.platformId === null
      ? ''
      : ` on ${facts.platformName}`;
  const base = `${facts.name} ${kind}${on}`;
  const status = titleStatus(facts, zone, now);
  const fits = [status, status.slice(0, 1)]
    .filter((parts) => parts.length > 0)
    .map((parts) => `${base} — ${parts.join(', ')}`)
    .find((head) => head.length <= TITLE_LENGTH);
  return `${fits ?? base} · ${BRAND_NAME}`;
}

/**
 * The raise's status for its title, most telling part first: ["82% funded",
 * "closes 22 Oct"], ["opens 2 Nov"], ["closed", "raised £130k"]. Funded as
 * the page's bar measures it ("12% of maximum" where a maximum is stated),
 * and only what the raise states. A status the sources do not settle reads
 * as live, as the page's clock has it.
 */
function titleStatus(facts: RaiseFacts, zone: string, now: Date): string[] {
  const money = (amount: number) => compactMoney(amount, facts.currency);
  switch (facts.phase) {
    case 'UPCOMING':
      return [
        facts.launchAt && facts.launchAt > now
          ? `opens ${dayMonth(facts.launchAt, zone)}`
          : 'opens soon',
      ];
    case 'CLOSED':
      return stated(facts.raised)
        ? ['closed', `raised ${money(facts.raised)}`]
        : ['closed'];
    case 'WITHDRAWN':
      return ['withdrawn'];
    case 'DELISTED':
      return ['no longer listed'];
    default: {
      const parts: string[] = [];
      const progress = progressOf(
        facts.raised,
        facts.target,
        facts.max,
        facts.currency,
      );
      if (progress) parts.push(`${progress.shown} ${progress.of}`);
      else if (stated(facts.raised))
        parts.push(`${money(facts.raised)} raised`);
      if (facts.closeAt && facts.closeAt > now) {
        parts.push(`closes ${dayMonth(facts.closeAt, zone)}`);
      }
      return parts;
    }
  }
}

/** One figure in the meta description, and how late it goes when it runs long. */
interface Clause {
  text: string;
  /** The lowest goes first. */
  keep: number;
}

/**
 * What the company does, then its figures: "Refillable home-cleaning
 * products, delivered in reusable steel bottles. £412k raised (82% funded),
 * 640 investors, £10 minimum, closes 22 Oct 2026." Within
 * DESCRIPTION_LENGTH: a long tagline is cut at a word, down to TAGLINE_FLOOR,
 * and past that the figures go, the minimum first, then the investors, the
 * dates and last the amount. Only what the raise states, so a missing figure
 * is left out, never shown as 0.
 *
 * The title above it names the company and the platform, so a tagline
 * stands alone. Without one (or with one that only repeats the name) the
 * raise is named as "Loopwell is raising on Crowdcube." An EDGAR row's
 * tagline is the adapter's "Offered through {intermediary}", which says
 * nothing of the company, so a filing says what it is instead: "Chuck Waste,
 * Inc filed a Form C with the SEC, offered through Fundify Portal, LLC."
 */
export function metaDescription(
  facts: RaiseFacts,
  zone: string,
  now: Date,
): string {
  const kept = clausesOf(facts, zone, now);
  const what = whatOf(facts, kept.length === 0);
  for (;;) {
    const figures = figuresText(kept);
    const room = DESCRIPTION_LENGTH - (figures ? figures.length + 1 : 0);
    const lead = what.fit(room);
    if (lead !== null) return figures ? `${lead} ${figures}` : lead;
    if (kept.length === 0) return what.last;
    const least = Math.min(...kept.map((c) => c.keep));
    kept.splice(
      kept.findIndex((c) => c.keep === least),
      1,
    );
  }
}

/**
 * The description's opening sentence: `fit` gives it within `room`
 * characters, or null where it cannot fit; `last` is it within the whole
 * description, for when even no figures leave it room. `alone`: the raise
 * states no figure to follow it.
 */
function whatOf(
  facts: RaiseFacts,
  alone: boolean,
): {
  fit: (room: number) => string | null;
  last: string;
} {
  const tagline = facts.filing
    ? null
    : facts.tagline?.replace(/\s+/g, ' ').trim();
  if (tagline && !sameAsName(tagline, facts.name)) {
    const floor = Math.min(TAGLINE_FLOOR, sentence(tagline).length);
    return {
      fit: (room) => (room >= floor ? sentenceWithin(tagline, room) : null),
      last: sentenceWithin(tagline, DESCRIPTION_LENGTH),
    };
  }
  // Longest first, so the intermediary goes before any figure does.
  const options = facts.filing
    ? filingSentences(facts)
    : alone
      ? [
          `${onPlatform(facts)}, tracked by ${BRAND_NAME}.`,
          `${onPlatform(facts)}.`,
        ]
      : [`${onPlatform(facts)}.`];
  return {
    fit: (room) => options.find((s) => s.length <= room) ?? null,
    last: cutAtWord(options[options.length - 1], DESCRIPTION_LENGTH),
  };
}

/**
 * "Chuck Waste, Inc filed a Form C with the SEC, offered through Fundify
 * Portal, LLC.", then the same without its intermediary.
 */
function filingSentences(facts: RaiseFacts): string[] {
  const filed = `${facts.name} filed a Form C with the SEC`;
  const offered = facts.tagline?.replace(/\s+/g, ' ').trim();
  return offered && /^offered through /i.test(offered)
    ? [`${filed}, o${offered.slice(1)}.`, `${filed}.`]
    : [`${filed}.`];
}

/**
 * The description's figures, in the order it says them: the amount raised
 * (or, with none, what the raise is for), the opening date of an upcoming
 * raise, the investors, the minimum, the close.
 */
function clausesOf(facts: RaiseFacts, zone: string, now: Date): Clause[] {
  const money = (amount: number) => compactMoney(amount, facts.currency);
  const clauses: Clause[] = [];
  const upcoming = facts.phase === 'UPCOMING';
  const progress = upcoming
    ? null
    : progressOf(facts.raised, facts.target, facts.max, facts.currency);
  const funded = progress && `${progress.shown} ${progress.of}`;
  if (!upcoming && stated(facts.raised)) {
    clauses.push({
      text: `${money(facts.raised)} raised${funded ? ` (${funded})` : ''}`,
      keep: 4,
    });
  } else if (funded) {
    clauses.push({ text: funded, keep: 4 });
  } else {
    // Nothing raised to say: what the raise is for, where it states it.
    const scale = scaleOf(facts.target, facts.max);
    const goal = [
      stated(facts.target) ? `target ${money(facts.target)}` : null,
      scale?.basis === 'maximum' ? `up to ${money(scale.amount)}` : null,
    ].filter((part) => part !== null);
    if (goal.length > 0) clauses.push({ text: goal.join(', '), keep: 4 });
  }
  if (upcoming && facts.launchAt && facts.launchAt > now) {
    clauses.push({
      text: `opens ${dayMonthYear(facts.launchAt, zone)}`,
      keep: 3,
    });
  }
  if (facts.investors !== null && facts.investors > 0) {
    clauses.push({
      text: plural(facts.investors, 'investor', 'investors'),
      keep: 2,
    });
  }
  if (stated(facts.minInvestment)) {
    clauses.push({ text: `${money(facts.minInvestment)} minimum`, keep: 1 });
  }
  // When it closed, which a raise that closed early did before its date. A
  // withdrawn or delisted raise did not close on its date at all.
  const closedOn = closedDateOf(facts, now);
  if (closedOn) {
    clauses.push({
      text: `closed ${dayMonthYear(closedOn, zone)}`,
      keep: 3,
    });
  } else if (
    facts.closeAt &&
    !ENDED.includes(facts.phase) &&
    facts.closeAt > now
  ) {
    clauses.push({
      text: `closes ${dayMonthYear(facts.closeAt, zone)}`,
      keep: 3,
    });
  }
  return clauses;
}

/** "£412k raised (82% funded), 640 investors."; null with no figures. */
function figuresText(clauses: Clause[]): string | null {
  if (clauses.length === 0) return null;
  const text = clauses.map((c) => c.text).join(', ');
  return `${text[0].toUpperCase()}${text.slice(1)}.`;
}

/**
 * The text with a full stop where it ends without one. A stop before a
 * closing quote, bracket or mark counts: "Whiskey. Without Limits.™".
 */
function sentence(text: string): string {
  return /[.!?…]["'”’)\]™®]*$/.test(text) ? text : `${text}.`;
}

/** The text as a sentence of at most `max` characters, cut at a word if it must be. */
function sentenceWithin(text: string, max: number): string {
  const whole = sentence(text);
  return whole.length <= max
    ? whole
    : cutAtWord(text, Math.min(max, text.length - 1));
}

/** At most `max` characters, cut at a word and marked "…" if cut at all. */
function cutAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const head = text.slice(0, max - 1);
  const space = head.lastIndexOf(' ');
  const cut = (space > 0 ? head.slice(0, space) : head).replace(
    /[\s,;:.–—-]+$/,
    '',
  );
  return `${cut}…`;
}

/** "22 Oct 2026", for the meta description. */
function dayMonthYear(date: Date, zone: string): string {
  const p = parts(date, zone, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${p.day} ${p.month} ${p.year}`;
}

/** Who is reading, as far as the follow card needs to know. */
export interface RaiseViewer {
  signedIn: boolean;
  /** The raise is on their watchlist. */
  following: boolean;
  /** Since when; null where they do not follow it or it is not known. */
  followedAt: Date | null;
  /** Their plan sends a follow's emails (the watchlist, Alerts and up). */
  alertsOn: boolean;
  /** Their address already gets the Weekly Sonar. */
  subscribed: boolean;
  /** What the upgrade link says: a trial they can still start, or not. */
  trialDays: number | null;
}

export const SIGNED_OUT: RaiseViewer = {
  signedIn: false,
  following: false,
  followedAt: null,
  alertsOn: false,
  subscribed: false,
  trialDays: null,
};

/**
 * The alert card's lead. A follow's emails are the Alerts plan's: on Free
 * the follow is kept and its emails held until the plan has them (the
 * reminder runs skip Free subscribers), so a reader who is not on it is told
 * which plan sends them.
 */
export function followBody(
  name: string,
  alertsOn: boolean,
): { wide: string; narrow: string } {
  return alertsOn
    ? {
        wide: `Follow ${name} in the Raise Sonar app and we’ll email you before the round closes, and straight away if the close date or target changes.`,
        narrow: `Follow ${name} in the Raise Sonar app for emails at 72 hours and 24 hours before close, and if the date or target changes.`,
      }
    : {
        wide: `Follow ${name} in the Raise Sonar app. On the Alerts plan, we’ll email you before the round closes, and straight away if the close date or target changes.`,
        narrow: `Follow ${name} in the Raise Sonar app. On the Alerts plan, you get emails at 72 hours and 24 hours before close, and if the date or target changes.`,
      };
}

/** "Close date moved from Thu 15 Oct to Thu 22 Oct." */
export function dateMoveNote(
  move: { from: Date; to: Date },
  zone: string,
): string {
  return `Close date moved from ${shortDay(move.from, zone)} to ${shortDay(move.to, zone)}.`;
}

/** The chips under the name: platform, instrument, then sector · country. */
export function chipsOf(facts: RaiseFacts): {
  platform: string;
  mono: string;
  instrument: string | null;
  where: string | null;
} {
  const verb =
    facts.phase === 'CLOSED'
      ? 'Raised on'
      : facts.phase === 'WITHDRAWN' || facts.phase === 'DELISTED'
        ? 'Listed on'
        : 'Raising on';
  return {
    platform: `${verb} ${facts.platformName}`,
    mono: facts.platformMono,
    instrument: facts.instrument,
    where: [facts.sector, facts.country].filter(Boolean).join(' · ') || null,
  };
}

// ---- More raises on the platform ----------------------------------------------

/** How many of the platform's other live raises the page shows. */
export const MORE_LIVE = 2;

/** How many of its upcoming raises. */
export const MORE_UPCOMING = 4;

/** Another live or upcoming raise on the same platform, as its card needs it. */
export interface OtherRaise {
  id: string;
  /** Its public slug: /raises/{slug}, where its name and Details go. */
  slug: string;
  name: string;
  tagline: string | null;
  /** Its logo in our store (RaiseLogoService), never the platform's address. */
  logo: LogoState;
  /** Announced, not yet taking money: its figure is reservations. */
  upcoming: boolean;
  currency: string | null;
  raised: number | null;
  target: number | null;
  investors: number | null;
  launchAt: Date | null;
  closeAt: Date | null;
}

/** Close date first, a raise with none last, then id, so the order is total. */
function closeOrder(
  a: Pick<OtherRaise, 'id' | 'closeAt'>,
  b: Pick<OtherRaise, 'id' | 'closeAt'>,
): number {
  const at = (r: Pick<OtherRaise, 'closeAt'>) =>
    r.closeAt?.getTime() ?? Number.POSITIVE_INFINITY;
  if (at(a) !== at(b)) return at(a) < at(b) ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Opening date first, a raise with none after, then as closeOrder. */
function openOrder(a: OtherRaise, b: OtherRaise): number {
  const at = (r: OtherRaise) =>
    r.launchAt?.getTime() ?? Number.POSITIVE_INFINITY;
  if (at(a) !== at(b)) return at(a) < at(b) ? -1 : 1;
  return closeOrder(a, b);
}

/**
 * Which of the platform's other live raises the page links to: the ones
 * closing next after this raise, in close-date order, round to the soonest
 * again after the last. Each live raise on the platform is then linked from
 * the one or two that close just before it, so a crawler that reaches one
 * of them reaches them all. A raise that has closed sorts before every live
 * one, so its page shows those closing soonest.
 */
export function pickOtherRaises(
  current: Pick<RaiseFacts, 'id' | 'closeAt'>,
  others: OtherRaise[],
  count = MORE_LIVE,
): OtherRaise[] {
  const sorted = others.filter((r) => r.id !== current.id).sort(closeOrder);
  const after = sorted.findIndex((r) => closeOrder(r, current) > 0);
  const start = after === -1 ? 0 : after;
  return [...sorted.slice(start), ...sorted.slice(0, start)].slice(0, count);
}

/**
 * A raise's logo, or its initials on a tile coloured from its name, as
 * views/partials/raise-tile.hbs draws it on both raise pages.
 */
export function tileOf(raise: { slug: string; name: string; logo: LogoState }) {
  return {
    logo: logoImg(raise),
    letters: initials(raise.name),
    style: tileStyle(raise.name),
  };
}

/** The tagline, unless it only says the name again. */
function descriptionOf(raise: OtherRaise): string | null {
  const text = raise.tagline?.trim();
  return text && !sameAsName(text, raise.name) ? text : null;
}

/** The bar as the templates draw it (views/partials/raise-bar.hbs). */
export function barView(bar: Bar) {
  return {
    below: bar.kind === 'below',
    past: bar.kind === 'past',
    unreported: bar.kind === 'unreported',
    width:
      bar.kind === 'below' ? bar.fill : bar.kind === 'past' ? bar.target : 0,
  };
}

/** A live raise's card (views/partials/raise-live-card.hbs). */
function liveCard(r: OtherRaise, now: Date, zone: string) {
  const pct = fundedPct(r.raised, r.target);
  const href = `/raises/${encodeURIComponent(r.slug)}`;
  return {
    id: r.id,
    name: r.name,
    href,
    details: href,
    tile: tileOf(r),
    description: descriptionOf(r),
    pill: r.closeAt ? urgencyOf(daysLeft(r.closeAt, now, zone)) : null,
    funded: pct === null ? null : { text: pctText(pct), past: pct >= 100 },
    // With no amount raised, what it is raising towards.
    target:
      pct === null && stated(r.target) ? fullMoney(r.target, r.currency) : null,
    closes: r.closeAt ? `closes ${shortDay(r.closeAt, zone)}` : null,
    bar: barView(barOf(r.raised, r.target)),
  };
}

/**
 * "More raises on Crowdcube": up to two of the platform's live raises,
 * picked by pickOtherRaises and shown closing soonest first, and up to four
 * of its upcoming ones, opening soonest first. Never the raise itself. A
 * group with no raises is left out; with neither, the section is (null).
 * Each card's name and Details are the raise's public page, which keeps
 * the raise pages linked to one another on this site.
 */
export function moreRaisesView(
  facts: Pick<
    RaiseFacts,
    'id' | 'slug' | 'closeAt' | 'platformId' | 'platformName'
  >,
  others: OtherRaise[],
  now: Date,
  zone: string,
) {
  const rest = others.filter((r) => r.id !== facts.id);
  const live = pickOtherRaises(
    facts,
    rest.filter((r) => !r.upcoming),
  ).sort(closeOrder);
  const upcoming = rest
    .filter((r) => r.upcoming)
    .sort(openOrder)
    .slice(0, MORE_UPCOMING);
  if (live.length === 0 && upcoming.length === 0) return null;

  const href = (r: OtherRaise) => `/raises/${encodeURIComponent(r.slug)}`;
  return {
    heading: `More raises on ${facts.platformName}`,
    all: facts.platformId
      ? {
          href: hubPath('platformRaises', {
            id: facts.platformId,
            name: facts.platformName,
          })!,
          label: `All ${facts.platformName} raises`,
        }
      : null,
    live: live.map((r) => liveCard(r, now, zone)),
    upcoming: upcoming.map((r) => ({
      id: r.id,
      name: r.name,
      href: href(r),
      details: href(r),
      tile: tileOf(r),
      description: descriptionOf(r),
      target: stated(r.target) ? fullMoney(r.target, r.currency) : null,
    })),
  };
}

// ---- A raise that has ended -------------------------------------------------------

/** How many raises an ended raise's page offers in its place. */
export const SIMILAR_OPEN = 6;

/** An open raise on the same platform or in the same sector. */
export type OpenCandidate = OtherRaise & {
  samePlatform: boolean;
  sameSector: boolean;
};

/** The word an ended raise's heading ends in; none while it runs. */
const ENDED_WORD: Partial<Record<DealPhase, string>> = {
  CLOSED: 'closed',
  WITHDRAWN: 'withdrawn',
  DELISTED: 'no longer listed',
};

/**
 * The page's h1: "Loopwell crowdfunding raise on Crowdcube (closed)", and the
 * company's name while the raise runs. A Form C whose portal we do not know
 * names no platform (as pageTitle has it).
 */
export function headingOf(
  facts: Pick<
    RaiseFacts,
    'name' | 'platformName' | 'platformId' | 'filing' | 'phase'
  >,
): string {
  const word = ENDED_WORD[facts.phase];
  if (!word) return facts.name;
  const on =
    facts.filing && facts.platformId === null
      ? ''
      : ` on ${facts.platformName}`;
  return `${facts.name} crowdfunding raise${on} (${word})`;
}

/**
 * The line under an ended raise's h1: "Closed 7 October 2026 — raised £412,300
 * from 640 investors (82% of target)". Only what the raise states, so a missing
 * figure takes its words with it. The percentage is of the target, else the
 * maximum. A withdrawn or delisted raise says how that is known and gives no
 * figures, like the rest of its page: they say nothing of how it ended.
 */
export function resultLine(
  facts: RaiseFacts,
  now: Date,
  zone: string,
): {
  lead: string;
  date: { iso: string; text: string } | null;
  rest: string;
} | null {
  const dated = (at: Date | null | undefined) =>
    at ? { iso: at.toISOString(), text: aboutDate(at, zone) } : null;
  const listing = `${facts.platformName}’s listing`;
  switch (facts.phase) {
    case 'CLOSED':
      return {
        lead: 'Closed',
        date: dated(closedDateOf(facts, now)),
        rest: closedFigures(facts),
      };
    case 'WITHDRAWN':
      return facts.withdrawnBy === 'LISTING_REMOVED'
        ? {
            lead: `Removed from ${listing}`,
            date: dated(facts.endedAt),
            rest: '',
          }
        : {
            lead: 'Withdrawn',
            date: dated(facts.endedAt),
            rest:
              facts.withdrawnBy === 'FORM_C_W'
                ? ' — Form C-W filed with the SEC'
                : '',
          };
    case 'DELISTED':
      return {
        lead: `Last seen on ${listing}`,
        date: dated(facts.endedAt),
        rest: '',
      };
    default:
      return null;
  }
}

/** " — raised £412,300 from 640 investors (82% of target)", as far as stated. */
function closedFigures(facts: RaiseFacts): string {
  const backers =
    facts.investors !== null && facts.investors > 0
      ? plural(facts.investors, 'investor', 'investors')
      : null;
  if (!stated(facts.raised)) return backers ? ` — ${backers}` : '';
  const scale = stated(facts.target)
    ? { amount: facts.target, word: 'target' }
    : stated(facts.max)
      ? { amount: facts.max, word: 'maximum' }
      : null;
  const pct = scale ? fundedPct(facts.raised, scale.amount) : null;
  return [
    ` — raised ${fullMoney(facts.raised, facts.currency)}`,
    backers ? ` from ${backers}` : '',
    scale && pct ? ` (${pctText(pct)} of ${scale.word})` : '',
  ].join('');
}

/**
 * Which open raises an ended raise's page offers: both on its platform and in
 * its sector first, then its sector, then its platform, each closing soonest
 * first, a raise with no close date last.
 */
export function pickSimilarOpen(
  candidates: OpenCandidate[],
  count = SIMILAR_OPEN,
): OpenCandidate[] {
  const rank = (r: OpenCandidate) =>
    r.samePlatform && r.sameSector ? 0 : r.sameSector ? 1 : 2;
  return [...candidates]
    .sort((a, b) => rank(a) - rank(b) || closeOrder(a, b))
    .slice(0, count);
}

/**
 * "Similar raises open now", with its alerts call to action, which stays when
 * no raise is open. As under the week's lists (weekListAlerts): signed out it
 * leads to an account, whose next step is its first rule, and says which plan
 * sends alerts; signed in, to the app's Alerts page.
 */
export function similarOpenView(
  facts: Pick<RaiseFacts, 'hubSector' | 'platformId' | 'platformName'>,
  candidates: OpenCandidate[],
  signedIn: boolean,
  appUrl: string,
  now: Date,
  zone: string,
) {
  const what =
    facts.hubSector?.name ?? (facts.platformId ? facts.platformName : null);
  return {
    heading: 'Similar raises open now',
    live: pickSimilarOpen(candidates).map((r) => liveCard(r, now, zone)),
    alerts: {
      heading: `Hear when the next ${what ? `${what} ` : ''}raise opens`,
      line: 'Set a rule and we email you the moment a raise that matches it goes live.',
      cta: {
        href: signedIn ? `${appUrl}alerts` : '/signup?from=ended_raise',
        label: 'Set up alerts',
      },
      note: signedIn
        ? null
        : 'Free to start. Alerts come with the Alerts plan.',
    },
  };
}

/** What the newsletter's done message needs from the page. */
export interface RaisePageInput {
  facts: RaiseFacts;
  viewer: RaiseViewer;
  now: Date;
  /** The reader's zone where the server knows it; London's otherwise. */
  zone: string | null;
  /** The dashboard, ending in a slash (res.locals.appUrl). */
  appUrl: string;
  /** "Monday 19 Oct at 08:00 London time": the Weekly Sonar's first issue. */
  firstIssue: string;
  /** The raise's logo in our store (RaiseLogoService.state). */
  logo: LogoState;
  /** The platform's other live and upcoming raises (RaisePageService.others). */
  others?: OtherRaise[];
  /** For a raise that has ended, open raises like it (RaisePageService.openLike). */
  openLike?: OpenCandidate[];
  /** Raises like it, for About this raise's comparison (SimilarRaisesService). */
  similar?: SimilarRaises | null;
}

/** Everything views/raise.hbs draws. */
export function raisePageView(input: RaisePageInput) {
  const { facts, viewer, now, appUrl } = input;
  const zone = safeZone(input.zone);
  const clockIn = clockOf(facts, viewer.following ? viewer.followedAt : null);
  const clock = clockView(clockIn, now, zone);
  const upcoming = facts.phase === 'UPCOMING';
  const ended = ENDED.includes(facts.phase);
  const progress = upcoming
    ? null
    : progressOf(facts.raised, facts.target, facts.max, facts.currency);
  const scale = scaleOf(facts.target, facts.max);
  const outbound = outboundUrl(facts.url);
  const linkName = facts.filing ? facts.sourceName : facts.platformName;
  // A filing's own link is SEC EDGAR's, which pays nobody.
  const outRel = outboundRel(!facts.filing && facts.affiliate);
  const following = viewer.signedIn && viewer.following;
  // A follow below Alerts is kept, and its emails held until the plan has them.
  const held = viewer.signedIn && !viewer.alertsOn;
  // The reminders are the reader's only when their plan sends them. Shown,
  // the strip's card carries the platform's link in place of the alert card.
  const stripShown = following && !held && clock.strip !== null;
  const note = `Investments are made on ${facts.platformName}. Raise Sonar doesn’t handle money or shares.`;

  return {
    name: facts.name,
    /** The h1, which says so once the raise has ended. */
    heading: headingOf(facts),
    ended,
    /** The line under an ended raise's h1. */
    result: resultLine(facts, now, zone),
    tagline: facts.tagline,
    logo: logoImg({ slug: facts.slug, name: facts.name, logo: input.logo }),
    chips: chipsOf(facts),
    // The client script runs the clock again on the reader's own.
    clockJson: JSON.stringify(clockIn),
    zone,
    clock,
    /** The "Last updated" <time>'s datetime; its text is `clock.updated`. */
    changedAt: clockIn.changedAt,
    stats: {
      raisedLabel: upcoming ? 'Reserved' : 'Raised',
      raised:
        facts.raised !== null && Number.isFinite(facts.raised)
          ? fullMoney(facts.raised, facts.currency)
          : null,
      /** Under the raised figure: "of €900,000 maximum", "of £500,000 target". */
      of: scale
        ? { amount: fullMoney(scale.amount, facts.currency), word: scale.basis }
        : null,
      /** With no amount raised reported, what the raise is raising towards. */
      goal: stated(facts.target)
        ? { label: 'Target', amount: fullMoney(facts.target, facts.currency) }
        : stated(facts.max)
          ? { label: 'Maximum', amount: fullMoney(facts.max, facts.currency) }
          : null,
      progress,
      tiles: tilesOf(facts),
      atTarget: atTargetLine(
        facts.valuation,
        facts.target,
        facts.currency,
        facts.converts,
      ),
    },
    /** About this raise, under the stats card: written from the figures above. */
    brief: aboutRaiseView(facts, input.similar ?? null, now, zone),
    /**
     * Questions about the raise, under About; null where its data answers
     * fewer than three. The page's JSON-LD repeats them (site/raise-json-ld.ts).
     */
    faq: raiseFaqView(facts, now, zone),
    move: facts.dateMove
      ? {
          extended: facts.dateMove.to > facts.dateMove.from,
          note: dateMoveNote(facts.dateMove, zone),
        }
      : null,
    about: facts.summary
      ? {
          heading: `About ${facts.name}`,
          source: `Summarised by Raise Sonar from ${facts.name}’s ${facts.platformName} pitch`,
          text: facts.summary,
          // An EDGAR row's own link is the filing; its pitch is its listing's.
          pitch: facts.pitchUrl
            ? {
                href: outboundUrl(facts.pitchUrl),
                label: `Read the full pitch on ${facts.platformName}`,
                // A filing's pitch is its listing's, on the platform.
                rel: outboundRel(facts.affiliate),
              }
            : null,
        }
      : null,
    alert: {
      id: facts.id,
      /** Open or about to open: the follow and its schedule are offered. */
      open: !ended,
      kicker: `Tracking ${facts.name}`,
      body: followBody(facts.name, viewer.alertsOn),
      schedule: clock.schedule,
      signedIn: viewer.signedIn,
      following,
      held,
      followLabel: `Follow ${facts.name}`,
      signUpHref: signUpHref(facts),
      logInHref: logInHref(facts),
      manageHref: `${appUrl}${facts.appPath}`,
      upgrade: held
        ? {
            href: `${appUrl}settings/billing`,
            label: viewer.trialDays
              ? `Try Alerts free for ${viewer.trialDays} days`
              : 'Upgrade to Alerts',
          }
        : null,
      newsletter: !viewer.subscribed,
      newsletterTitle: ended
        ? 'Get the free weekly newsletter'
        : 'Or get the free weekly newsletter',
      newsletterBody: `This email signup gives you the weekly newsletter: new raises and raises closing soon. It doesn’t send alerts for ${facts.name}.`,
      firstIssue: input.firstIssue,
      outbound,
      outRel,
      linkName,
      note,
      /** The link moves to the strip's card while the strip shows. */
      outHidden: stripShown,
    },
    strip: clock.strip ? { ...clock.strip, shown: stripShown } : null,
    cta: {
      href: outbound,
      rel: outRel,
      label: `View the raise on ${linkName}`,
      note,
    },
    // An ended raise's page offers open raises like it in place of the
    // platform's other raises, which it would repeat.
    more: ended ? null : moreRaisesView(facts, input.others ?? [], now, zone),
    similarOpen: ended
      ? similarOpenView(
          facts,
          input.openLike ?? [],
          viewer.signedIn,
          appUrl,
          now,
          zone,
        )
      : null,
  };
}
